<template>
  <div class="animated fadeIn">
    <b-form @submit.prevent="login" novalidate autocomplete="off">
      <b-card header-tag="header">
        <div slot="header">
          <h2>Login</h2>
        </div>

        <b-row>
          <b-col>
            <b-form-group>
              <label for="username" class="eden__label__required">Email</label>
              <b-form-input
                id="username"
                ref="username"
                v-model.trim="form.username"
                :state="$v.form.username.$dirty ? !$v.form.username.$error : null"
                aria-describedby="usernameFeedback"
                placeholder="Email"
                autocomplete="nope"
                autocorrect="off"
                autocapitalize="off"></b-form-input>
              <b-form-invalid-feedback id="usernameFeedback">
                <span v-if="!$v.form.username.required">This is a required field</span>
                <span v-else-if="!$v.form.username.email">Please enter a valid email address</span>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <b-form-group>
              <label for="password" class="eden__label__required">
                Password
              </label>
              <password-toggle-input
                id="password"
                ref="password"
                v-model="form.password"
                placeholder="Password"></password-toggle-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="isStaging">
          <b-col>
            <b-form-group>
              <b-form-checkbox v-model="ackStaging">
                I acknowledge this is the <strong>staging/test</strong> version of the portal and <strong>NOT</strong> for production use. 
                <br /><br />
                This environment may be periodically reset or unavailable!!<br /><br />
                To login to production, please use the main login page at <a href="https://portal.holisticindustries.net/login" >https://portal.holisticindustries.net/login</a>.
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-4">
          <b-col cols="12" xl="2" lg="3" md="6">
            <b-form-group>
              <b-button
                type="submit"
                ref="save"
                id="save"
                variant="primary"
                :disabled="!formIsValid || (isStaging && !ackStaging)">
                <i class="fa fa-user mr-2"></i>
                Login
              </b-button>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <button
              type="button"
              class="btn btn-info"
              @click="onAzureLoginClick"
            >
              <CIcon name="cib-microsoft" class="mr-2" />
              Login with Office 365
            </button>
          </b-col>
        </b-row>

        <b-row class="mt-4">
          <b-col cols="12">
            <a href="/forgot-password">Forgot password? Click here.</a>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <div id="recaptcha-container"></div>
          </b-col>
        </b-row>
      </b-card>
    </b-form>
  </div>
</template>
<script>
import { validationMixin } from 'vuelidate'
import { required, email as emailValidator } from 'vuelidate/lib/validators'
import { v4 as uuidv4 } from 'uuid'
import { getRecaptchaToken } from '@/utils/recaptcha'

export default {
  mixins: [
    validationMixin,
  ],

  data() {
    return {
      ackStaging: false,
      isStaging: window.location.hostname.includes('staging') ||
                window.location.hostname.includes('dev-portal'),
      recaptchaToken: null,
      form: {
        username: null,
        password: null,
      },
    }
  },

  validations: {
    form: {
      username: { required, email: emailValidator },
      password: { required },
    },
  },

  computed: {
    formIsValid() {
      // Standard validation
      const baseValid = !this.$v.$invalid

      // Additional staging requirement
      if (this.isStaging) {
        return baseValid && this.ackStaging
      }

      return baseValid
    }
  },

  methods: {
    async login() {
      // Form validation
      this.$v.form.$touch()
      if (!this.formIsValid) return

      if (this.$v.form.$anyError) {
        this.$toasted.global.error({
          message: 'Please correct errors in form',
        })
        return
      }

      if (typeof grecaptcha === 'undefined') {
        this.$log.error('grecaptcha is not loaded');
        this.showRecaptchaNotAvailableToast()
        return;
      }
      if (window.location.hostname !== 'localhost') {
        const token = await getRecaptchaToken('LOGIN', this.$log)
        if (!token) {
          this.showRecaptchaTokenErrorToast()
          return
        }

        this.form.recaptchaToken = token
      }

      const loader = this.$loading.show()
      try {
        const { data } = await this.$request({
          url: '/api/auth/login',
          method: 'post',
          data: this.form,
        })
        this.$store.commit('authToken', data?.token)
        this.$store.commit('userProfile', null)   // ✅ clear cached profile in Vuex

        // Store our app version in local storage
        try {
          const res = await this.$request({
            method: 'get',
            url: '/build_version.json',
            headers: {
              Authorization: `Bearer ${data?.token}`,
            },
            params: {
              __ts: Date.now(),
            },
          })

          this.$log.info(`Stored app version: ${this.$store.getters.appVersion}`)
          this.$log.info(`Deployed app version: ${res.data.version}`)
          this.$store.commit('appVersion', res.data.version)
        } catch (e) {
          this.$log.error('Error storing app version', e)
        }

        // TODO: Router Hijack
        // TODO: Axios Hijack

        // Redirect if we ended up here from a session expire
        try {
          if (this.$store.getters.loginRedirect) {
            const redir = this.$store.getters.loginRedirect
            this.$store.commit('loginRedirect', null)
            return window.location.href = redir
          }
        } catch (e) {
          this.$log.error('Error redirecting user', e)
        }

        // Redirect if we ended up here with a "redirect" query param
        try {
          const redirectParam = this.$route.query.redirect
          if (redirectParam) {
            return window.location.href = redirectParam
          }
        } catch (e) {
          this.$log.error('Error redirecting user via query param', e)
        }

        this.$router.replace('/')
      } catch (e) {
        this.$log.error('Error logging in user', e)
        this.$toasted.global.error({
          message: 'Invalid credentials',
        })
        return
      } finally {
        loader.hide()
      }
    },

    async onAzureLoginClick() {
      try {
        const redirectUri = encodeURIComponent(`${window.location.origin}/login/azuread`);
        const { pkce } = this.$store.getters;
    
        let token = null;
        let url = `/api/auth/azuread/redirect?redirectUri=${redirectUri}&codeChallenge=${pkce.codeChallenge}`;

        if (typeof grecaptcha === 'undefined') {
          this.$log.error('grecaptcha is not loaded');
          this.showRecaptchaNotAvailableToast()
          return;
        }
        if (window.location.hostname !== 'localhost') {
          token = await getRecaptchaToken('LOGIN', this.$log)
          if (!token) {
            this.showRecaptchaTokenErrorToast()
            return
          }
          url = `/api/auth/azuread/redirect?redirectUri=${redirectUri}&codeChallenge=${pkce.codeChallenge}&recaptchaToken=${token}`;
        }
        window.location.href = url;

      } catch (e) {
        this.$log.error('onAzureLoginClick failed', e);
        this.$toasted.global.error({ message: 'Office 365 login failed. Please try again.' });
      }
    },

    showRecaptchaNotAvailableToast() {
      this.$toasted.global.error({message: 'reCAPTCHA is not available. Please try again later.',
        position: 'top-right',
        duration: 5000,
      });
    },

    showRecaptchaTokenErrorToast() {
      this.$toasted.global.error({
        message: 'Could not retrieve reCAPTCHA verification token. Please try again.',
        position: 'top-right',
        duration: 5000,
      });
    },
  },

  async mounted() {
    await this.$store.dispatch('generatePkce');
  },
}
</script>
